import React from "react";
import { Link } from "react-router-dom";
import { Star, Users, Radio, Clock, ExternalLink, GraduationCap } from "lucide-react";

const DELIVERY_LABEL = {
  training_and_exam: null,
  exam_only: { label: "Exam only", cls: "bg-[var(--navy)] text-[var(--gold-soft)]" },
  direct_assessment: { label: "Chartered route", cls: "bg-[var(--gold-deep)] text-white" },
};

export default function CourseCard({ course }) {
  const totalMin = (course.lessons || []).reduce((s, l) => s + (l.duration_min || 0), 0);
  const totalHours = course.learning_hours || Math.round(totalMin / 60);
  const delivery = DELIVERY_LABEL[course.delivery_type];
  const isBadge = typeof course.cover_url === "string" && course.cover_url.startsWith("/badges/");
  return (
    <Link
      to={`/courses/${course.slug || course.id}`}
      className="card-warm p-4 flex flex-col gap-3"
      data-testid={`course-card-${course.id}`}
    >
      <div className="relative aspect-[16/10] rounded-xl overflow-hidden"
           style={{ background: isBadge
             ? "radial-gradient(ellipse at center, #22365F 0%, #0F2A54 55%, #091B38 100%)"
             : "var(--muted-surface)" }}>
        {course.cover_url ? (
          isBadge ? (
            <img src={course.cover_url} alt={course.title} className="w-full h-full object-contain p-4 drop-shadow-[0_6px_18px_rgba(201,165,87,0.35)]"/>
          ) : (
            <img src={course.cover_url} alt={course.title} className="w-full h-full object-cover" />
          )
        ) : (
          <div className="w-full h-full grid place-items-center text-zinc-400">No cover</div>
        )}
        {course.is_live && !delivery && (
          <div className="absolute top-3 left-3 bg-[var(--navy)] text-white text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full inline-flex items-center gap-1.5">
            <span className="live-dot" /> Live
          </div>
        )}
        {delivery && (
          <div className={`absolute top-3 left-3 text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 ${delivery.cls}`}>
            {course.delivery_type === "exam_only" ? <ExternalLink size={11}/> : <GraduationCap size={11}/>} {delivery.label}
          </div>
        )}
        <div className="absolute top-3 right-3 bg-white/95 text-[var(--ink)] text-xs font-bold px-2.5 py-1 rounded-full">
          {course.price > 0 ? `€${course.price}` : "Enquire"}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="eyebrow">{course.category}</div>
        <h3 className="font-display text-xl leading-snug text-[var(--ink)]">{course.title}</h3>
        <p className="text-sm text-zinc-600 line-clamp-2">{course.description}</p>
        <div className="flex items-center flex-wrap gap-3 text-xs text-zinc-600 pt-1">
          <span className="inline-flex items-center gap-1"><Star size={13} className="text-[var(--gold-deep)]"/> {course.rating || 4.8}</span>
          <span className="inline-flex items-center gap-1"><Users size={13}/> {course.enrolled_count || 0}</span>
          {course.delivery_type === "training_and_exam" ? (
            <>
              <span className="inline-flex items-center gap-1" data-testid="course-card-format"><Radio size={13}/> Live sessions + exam</span>
              <span className="inline-flex items-center gap-1"><Clock size={13}/> {totalHours}h</span>
            </>
          ) : (
            <span className="inline-flex items-center gap-1" data-testid="course-card-format"><ExternalLink size={13}/> Online exam</span>
          )}
        </div>
      </div>
    </Link>
  );
}
